/* Native scrolling owns touch momentum; controls use the same bounded snap stops. */
(() => {
  if (window.q28PdpSliders) { window.q28PdpSliders.scan(document); return; }
  const initialized = new WeakSet();
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  function initialize(root, kind) {
    const track = root.querySelector(kind === 'ritual' ? '.night-ritual__track' : '[data-comparison-grid]');
    const dots = root.querySelector(kind === 'ritual' ? '[data-dots]' : '[data-comparison-dots]');
    if (!track || !dots || initialized.has(track)) return;
    initialized.add(track);
    const cards = [...track.querySelectorAll(kind === 'ritual' ? '.night-ritual__card' : '[data-comparison-card]')];
    const previous = root.querySelector('[data-prev]');
    const next = root.querySelector('[data-next]');
    const highlight = track.querySelector('.comparison-table__column--highlight');
    let stops = [], controls = [], frame = 0, dragging = null;
    dots.removeAttribute('aria-hidden');
    dots.setAttribute('role', 'group');
    dots.setAttribute('aria-label', kind === 'ritual' ? 'Instruction navigation' : 'Comparison navigation');
    track.tabIndex = 0;
    function closest() {
      let best = 0;
      stops.forEach((stop, index) => {
        if (Math.abs(stop.left - track.scrollLeft) < Math.abs(stops[best].left - track.scrollLeft)) best = index;
      });
      return best;
    }
    function update() {
      const active = closest();
      controls.forEach((control, index) => {
        control.classList.toggle('is-active', index === active);
        control.setAttribute('aria-current', String(index === active));
      });
      if (previous) previous.disabled = active === 0;
      if (next) next.disabled = active >= stops.length - 1;
    }
    function go(index) {
      const stop = stops[Math.max(0, Math.min(stops.length - 1, index))];
      if (stop) track.scrollTo({ left: stop.left, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }
    function rebuild() {
      frame = 0;
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      const rect = track.getBoundingClientRect();
      if (!rect.width) return;
      const css = getComputedStyle(track);
      const sticky = highlight && getComputedStyle(highlight).position === 'sticky';
      let inset = parseFloat(css.scrollPaddingLeft) || 0;
      if (sticky) {
        inset = highlight.getBoundingClientRect().right - rect.left + (parseFloat(css.columnGap) || 0);
        track.style.scrollPaddingLeft = `${inset}px`;
      }
      const positions = [{ left: 0, card: 0 }];
      cards.forEach((card, index) => {
        if (sticky && card === highlight) return;
        const left = Math.max(0, Math.min(max, track.scrollLeft + card.getBoundingClientRect().left - rect.left - inset));
        if (!positions.some(stop => Math.abs(stop.left - left) < 2)) positions.push({ left, card: index });
      });
      positions.sort((a, b) => a.left - b.left);
      stops = max < 2 || css.overflowX === 'visible' ? [positions[0]] : positions;
      const visible = stops.length > 1;
      dots.hidden = !visible;
      if (controls.length !== stops.length) {
        dots.replaceChildren();
        controls = stops.map((stop, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = kind === 'ritual' ? 'night-ritual__dot' : 'comparison-table__dot';
          if (kind !== 'ritual') button.setAttribute('data-dot', '');
          button.style.cssText = 'padding:0;border:0;cursor:pointer;flex-shrink:0';
          button.addEventListener('click', () => go(index));
          dots.append(button);
          return button;
        });
      }
      controls.forEach((button, index) => button.setAttribute('aria-label', `${kind === 'ritual' ? 'Instruction' : 'Comparison'} position ${index + 1} of ${stops.length}`));
      update();
    }
    const schedule = () => { if (!frame) frame = requestAnimationFrame(rebuild); };
    track.addEventListener('scroll', update, { passive: true });
    if (previous) previous.addEventListener('click', () => go(closest() - 1));
    if (next) next.addEventListener('click', () => go(closest() + 1));
    track.addEventListener('keydown', event => {
      if (event.target !== track || stops.length < 2) return;
      const actions = { ArrowLeft: closest() - 1, ArrowRight: closest() + 1, Home: 0, End: stops.length - 1 };
      if (event.key in actions) { event.preventDefault(); go(actions[event.key]); }
    });
    // Native touch scrolling remains untouched. Mouse dragging uses one snap on release.
    track.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'mouse' || event.button || stops.length < 2 || event.target.closest('a,button,input,select,textarea')) return;
      dragging = { id: event.pointerId, x: event.clientX, left: track.scrollLeft, active: false, snap: track.style.scrollSnapType, behavior: track.style.scrollBehavior };
    });
    track.addEventListener('pointermove', event => {
      if (!dragging || event.pointerId !== dragging.id) return;
      if (!dragging.active && Math.abs(event.clientX - dragging.x) < 6) return;
      if (!dragging.active) {
        dragging.active = true;
        track.setPointerCapture(event.pointerId);
        track.style.scrollSnapType = 'none';
        track.style.scrollBehavior = 'auto';
      }
      event.preventDefault();
      track.scrollLeft = dragging.left + dragging.x - event.clientX;
    });
    function endDrag(event) {
      if (!dragging || event.pointerId !== dragging.id) return;
      const gesture = dragging;
      dragging = null;
      if (!gesture.active) return;
      const index = closest();
      track.style.scrollSnapType = gesture.snap;
      track.style.scrollBehavior = gesture.behavior;
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
      go(index);
    }
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('dragstart', event => { if (stops.length > 1) event.preventDefault(); });
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('resize', schedule, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(schedule).observe(track);
    root.addEventListener('load', schedule, true);
    if (document.fonts) document.fonts.ready.then(schedule);
    rebuild();
  }
  function scan(scope) {
    scope.querySelectorAll('[data-night-ritual]').forEach(root => initialize(root, 'ritual'));
    scope.querySelectorAll('[data-comparison-grid]').forEach(track => {
      const root = track.closest('[id^="comparison-table-"]');
      if (root) initialize(root, 'comparison');
    });
  }
  window.q28PdpSliders = { scan };
  scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
})();
